<template>
    <tailwind-layout title="Lease Metrics">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Lease Metrics</h1>
                <p class="mt-1 text-sm text-gray-600">
                    RocketLawyer lease activity at a glance. "Generated" is a completed, downloadable lease;
                    "started" is a wizard that was opened. Week runs Monday–Sunday (from {{ weekStartLabel }}).
                </p>
            </header>

            <!-- Headline: generated -->
            <section class="space-y-2">
                <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Leases Generated</h2>
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div class="rounded-xl border-2 border-rent-risk-blue-400 bg-rent-risk-blue-100/40 p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-rent-risk-blue-400">This Week</p>
                        <p class="mt-1 text-3xl font-bold text-gray-900">{{ headline.generated.this_week }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Last Week</p>
                        <p class="mt-1 text-3xl font-bold text-gray-900">{{ headline.generated.last_week }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">This Month</p>
                        <p class="mt-1 text-3xl font-bold text-gray-900">{{ headline.generated.this_month }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">All Time</p>
                        <p class="mt-1 text-3xl font-bold text-gray-900">{{ headline.generated.all_time }}</p>
                    </div>
                </div>
            </section>

            <!-- Secondary: started + live pipeline -->
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section class="space-y-2">
                    <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Leases Started</h2>
                    <div class="grid grid-cols-3 gap-4">
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">This Week</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ headline.started.this_week }}</p>
                        </div>
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Last Week</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ headline.started.last_week }}</p>
                        </div>
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">All Time</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ headline.started.all_time }}</p>
                        </div>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Live Pipeline</h2>
                    <div class="grid grid-cols-3 gap-4">
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">In Progress</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ pipeline.in_progress }}</p>
                        </div>
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-orange-500">Out for Sig.</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ pipeline.out_for_signature }}</p>
                        </div>
                        <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <p class="text-xs font-semibold uppercase tracking-wider text-green-600">Executed</p>
                            <p class="mt-1 text-2xl font-bold text-gray-900">{{ pipeline.fully_executed }}</p>
                        </div>
                    </div>
                </section>
            </div>

            <!-- Signer summary -->
            <section class="space-y-2">
                <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Signers (on generated leases)</h2>
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-5">
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Total</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ signers.total }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Landlords</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ signers.landlords }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Tenants</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ signers.tenants }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Signed</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ signers.signed }}</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Avg / Lease</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ signers.avg_per_lease }}</p>
                    </div>
                </div>
            </section>

            <!-- Payment: subscription vs one-time $49 -->
            <section class="space-y-2">
                <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">How Generated Leases Were Paid For</h2>
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">Via Subscription</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ payment.subscription }}</p>
                        <p class="mt-0.5 text-xs text-gray-400">RiskGuard Pro — no fee</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">One-Time $49</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ payment.one_time }}</p>
                        <p class="mt-0.5 text-xs text-gray-400">Paid per lease</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">$49 Payers</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ payment.one_time_paying_users }}</p>
                        <p class="mt-0.5 text-xs text-gray-400">Distinct users</p>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">One-Time Revenue</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">${{ payment.one_time_revenue }}</p>
                        <p class="mt-0.5 text-xs text-gray-400">Sum of lease fees</p>
                    </div>
                </div>
            </section>

            <!-- Other documents (eviction notices / general forms, RAC-10324) -->
            <section v-if="otherDocuments && otherDocuments.length" class="space-y-2">
                <h2 class="px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">Other Documents Generated</h2>
                <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div
                        v-for="doc in otherDocuments"
                        :key="doc.category"
                        class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                    >
                        <p class="text-xs font-semibold uppercase tracking-wider text-gray-400">{{ doc.label }}</p>
                        <p class="mt-1 text-2xl font-bold text-gray-900">{{ doc.total }}</p>
                        <p class="mt-0.5 text-xs text-gray-400">All time</p>
                    </div>
                </div>
            </section>

            <!-- Breakdowns: state + signing status -->
            <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <section class="space-y-2">
                    <h2 class="text-lg font-semibold text-gray-900">Generated by State</h2>
                    <div class="overflow-hidden rounded-md shadow lg:border">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-blue-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">State</th>
                                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider">Leases</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="row in byState" :key="row.state" class="bg-white hover:bg-gray-100">
                                    <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ row.state }}</td>
                                    <td class="px-6 py-3 text-right text-sm text-gray-700">{{ row.total }}</td>
                                </tr>
                                <tr v-show="!byState.length">
                                    <td colspan="2" class="px-6 py-4 text-center text-sm font-medium text-gray-400">
                                        No generated leases yet.
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section class="space-y-2">
                    <h2 class="text-lg font-semibold text-gray-900">Generated by Signing Status</h2>
                    <div class="overflow-hidden rounded-md shadow lg:border">
                        <table class="min-w-full divide-y divide-gray-300">
                            <thead class="bg-blue-50">
                                <tr>
                                    <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                                    <th scope="col" class="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider">Leases</th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200">
                                <tr v-for="row in bySigningStatus" :key="row.status" class="bg-white hover:bg-gray-100">
                                    <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ row.label }}</td>
                                    <td class="px-6 py-3 text-right text-sm text-gray-700">{{ row.total }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            <!-- Recent leases -->
            <section class="space-y-2">
                <h2 class="text-lg font-semibold text-gray-900">Recent Leases</h2>
                <div class="overflow-x-auto rounded-md shadow lg:border">
                    <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-blue-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Property</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">State</th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-bold uppercase tracking-wider">Tenants</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Started</th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">Generated</th>
                            </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200">
                            <tr v-for="lease in recent" :key="lease.id" class="bg-white hover:bg-gray-100">
                                <td class="px-6 py-3 text-sm font-medium text-gray-900">{{ lease.property }}</td>
                                <td class="px-6 py-3 text-sm text-gray-700">{{ lease.state }}</td>
                                <td class="px-6 py-3 text-right text-sm text-gray-700">{{ lease.tenants }}</td>
                                <td class="px-6 py-3 text-sm">
                                    <span class="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold" :class="statusPill(lease)">
                                        {{ statusLabel(lease) }}
                                    </span>
                                </td>
                                <td class="px-6 py-3 text-sm text-gray-500">{{ lease.created_at }}</td>
                                <td class="px-6 py-3 text-sm text-gray-500">{{ lease.completed_at || '—' }}</td>
                            </tr>
                            <tr v-show="!recent.length">
                                <td colspan="6" class="px-6 py-4 text-center text-sm font-medium text-gray-400">
                                    No leases yet.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    </tailwind-layout>
</template>

<script setup>
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

defineProps({
    generatedAt: String,
    weekStartLabel: String,
    headline: Object,
    pipeline: Object,
    byState: Array,
    bySigningStatus: Array,
    signers: Object,
    payment: Object,
    recent: Array,
    otherDocuments: Array,
})

const statusLabel = (lease) => {
    if (lease.status !== 'completed') {
        return lease.status === 'in_progress' ? 'In Progress' : 'Abandoned'
    }
    return {
        signed: 'Fully Executed',
        partially_signed: 'Partially Signed',
        sign_in_progress: 'Out for Signature',
        in_preparation: 'Ready to Send',
    }[lease.signing_status] || 'Generated'
}

const statusPill = (lease) => {
    if (lease.status !== 'completed') {
        return 'bg-gray-100 text-gray-600'
    }
    return {
        signed: 'bg-green-100 text-green-700',
        partially_signed: 'bg-orange-100 text-orange-600',
        sign_in_progress: 'bg-orange-100 text-orange-600',
        in_preparation: 'bg-rent-risk-blue-100 text-rent-risk-blue-400',
    }[lease.signing_status] || 'bg-rent-risk-blue-100 text-rent-risk-blue-400'
}
</script>
